import { Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { Loader } from '@/components/layout/loading';
import OptionMenu from '@/components/option-menu/option-menu';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { useSourceAccounts } from '@/contexts/useSourceAccounts';
import { useWorkspace } from '@/contexts/useWorkspace';
import { CloudProviders } from '@/data/inventory/data';

export const S3BucketBackupTypeField = ({
  item,
}: {
  item: InventoryResource;
}) => {
  const dal = useDAL();
  const { snackbar } = useWorkspace();
  const { sourceAccounts } = useSourceAccounts();
  const { data: itemConfigBody, mutate } = dal.inventory.configuration.get(
    item.id
  );

  const { isAuthorized } = useRoles();
  const [isUpdating, setIsUpdating] = useState(false);

  const accountId = sourceAccounts?.find(
    (x) => x.providerAccountId === item.providerAccountId
  )?.id;

  const { data: accountConfigBody } = dal.cloudAccounts.source.config.get(
    accountId || ''
  );

  return (
    <Stack direction='row' alignItems='center' gap='12px'>
      <Typography>
        {accountConfigBody?.enableCdcBackup && itemConfigBody?.enableCdcBackup
          ? `Event notifications from ${CloudProviders[item.cloudProvider]?.displayName}`
          : 'Full bucket scan'}
      </Typography>
      {isUpdating && (
        <Loader variant='inline' sx={{ height: '32px !important' }} />
      )}
      {accountConfigBody?.enableCdcBackup && itemConfigBody && !isUpdating && (
        <OptionMenu
          options={[
            {
              id: 'update-cdc-backup',
              text: itemConfigBody.enableCdcBackup
                ? 'Use full bucket scan'
                : `Use event notifications from ${CloudProviders[item.cloudProvider]?.displayName}`,
              menuItemProps: {
                disabled: !isAuthorized('update:inventory'),
                onClick: () => {
                  setIsUpdating(true);
                  void dal.inventory.configuration
                    .update(item.id, {
                      ...itemConfigBody,
                      enableCdcBackup: !itemConfigBody.enableCdcBackup,
                    })
                    .then(() => mutate())
                    .catch((error) => {
                      let errorMessage = 'Failed to update backup type';

                      if (
                        error &&
                        typeof error === 'object' &&
                        'code' in error
                      ) {
                        const apiError = error as {
                          code: number;
                          message?: string;
                        };
                        if (apiError.code === 403) {
                          errorMessage =
                            'Insufficient permissions to set up event notifications. Please update the source account role and try again.';
                        } else if (apiError.message) {
                          errorMessage = apiError.message;
                        }
                      }

                      snackbar.showErrorMessage(errorMessage);
                    })
                    .finally(() => setIsUpdating(false));
                },
              },
            },
          ]}
        />
      )}
    </Stack>
  );
};
